import type { LocationQuery, LocationQueryValue } from 'vue-router';

/**
 * The directory's query-string vocabulary (`?keyword=tafe&states=NSW,QLD&sector=TAFE Institute&page=2`).
 * State codes travel as one comma list; sectors are the index's own labels, which may carry
 * commas, so each travels as its own `sector` value. Anything unrecognised reads as "not set".
 */

type QueryValue = LocationQueryValue | LocationQueryValue[] | undefined;

const KEYWORD_MAX_LENGTH = 100;
const SECTOR_MAX_LENGTH = 100;
// Mirrors InstitutionDirectoryQuery::MAX_PAGE, which refuses anything higher.
const MAX_PAGE = 500;

const readString = (value: QueryValue): string => (typeof value === 'string' ? value : '');

const readStrings = (value: QueryValue): string[] => {
  const values = Array.isArray(value) ? value : [value];
  return values.filter((item): item is string => typeof item === 'string');
};

export const parseGuidePage = (value: QueryValue): number => {
  const page = Number.parseInt(readString(value), 10);
  return Number.isInteger(page) && page > 0 ? Math.min(page, MAX_PAGE) : 1;
};

export const parseGuideKeyword = (value: QueryValue): string => readString(value).slice(0, KEYWORD_MAX_LENGTH);

const parseList = (value: QueryValue): string[] => readString(value)
  .split(',')
  .map(item => item.trim())
  .filter(item => item !== '');

export const parseGuideStateCodes = (value: QueryValue): string[] => [...new Set(parseList(value).map(code => code.toUpperCase()))];

/** Sector labels as the index spells them, de-duplicated; a blank or oversized value is dropped. */
export const parseGuideSectors = (value: QueryValue): string[] => [...new Set(
  readStrings(value)
    .map(item => item.trim())
    .filter(item => item !== '' && item.length <= SECTOR_MAX_LENGTH),
)];

/** Drops the keys whose value is unset so the query string only carries what differs from the default. */
export const compactGuideQuery = (query: Record<string, string | string[] | undefined>): LocationQuery =>
  Object.fromEntries(Object.entries(query).filter((entry): entry is [string, string | string[]] =>
    entry[1] !== undefined && entry[1] !== '' && !(Array.isArray(entry[1]) && entry[1].length === 0)));
